import React, { useEffect, useRef, useState } from 'react';
import { Search, Eye, X, GripVertical } from 'lucide-react';
import { Calendar as MiniCal } from '@/components/ui/calendar';
import {
  VIEW_TYPES, toISO, addDays, locationSelectionState,
} from './calendarUtils';
import { matchesFreeText } from '@/components/search/SearchBuilder';

const Section = ({ title, action, children }) => (
  <div className="border-t border-slate-200 pt-2 mt-2">
    <div className="flex items-center justify-between px-1 mb-1">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const ListItem = ({
  selected, onClick, onRemove, children, testId,
  draggable, onDragStart, onDragOver, onDrop, onDragEnd, dragging,
}) => (
  <div
    className={`flex items-center gap-1.5 px-1.5 py-1 rounded text-xs cursor-pointer ${
      selected ? 'bg-slate-200 font-semibold text-slate-900' : 'hover:bg-slate-100 text-slate-700'
    } ${dragging ? 'opacity-50' : ''}`}
    onClick={(e) => onClick?.(e)}
    onDragOver={onDragOver}
    onDrop={onDrop}
    data-testid={testId}
  >
    {draggable && (
      <button
        type="button"
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="text-slate-400 hover:text-slate-600 shrink-0 cursor-grab active:cursor-grabbing touch-none"
        title="Reihenfolge ändern (ziehen)"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testId}-drag`}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
    )}
    <span className="w-2 h-2 rounded-full bg-brand-red shrink-0" />
    <span className="flex-1 truncate">{children}</span>
    {onRemove && (
      <button
        type="button"
        className="text-rose-500 hover:text-rose-700 shrink-0"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        title="Aus Liste entfernen"
      >
        <X className="w-3 h-3" />
      </button>
    )}
  </div>
);

function SelectionRow({
  checked, indeterminate = false, onToggle, indent = 0, children, testId,
  draggable, onDragStart, onDragOver, onDrop, onDragEnd, dragging, onRemove,
}) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate, checked]);

  return (
    <div
      className={`flex items-center gap-1 rounded text-xs ${dragging ? 'opacity-50' : ''}`}
      style={{ paddingLeft: `${indent * 12 + 4}px` }}
      data-testid={testId}
    >
      {draggable && (
        <button
          type="button"
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="text-slate-400 hover:text-slate-600 shrink-0 cursor-grab active:cursor-grabbing touch-none p-0.5"
          title="Reihenfolge ändern (ziehen)"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-drag`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
      )}
      <label className="flex items-center gap-1.5 flex-1 min-w-0 py-1 pr-1 cursor-pointer hover:bg-slate-100 rounded">
        <input
          ref={inputRef}
          type="checkbox"
          className="shrink-0"
          checked={checked}
          onChange={onToggle}
          data-testid={`${testId}-checkbox`}
        />
        <span className="truncate text-slate-800">{children}</span>
      </label>
      {onRemove && (
        <button
          type="button"
          className="text-rose-500 hover:text-rose-700 shrink-0 p-0.5"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Aus Liste entfernen"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export default function CalendarSidebar({
  viewType, setViewType,
  raster, setRaster,
  beginHour, setBeginHour,
  currentDate, setCurrentDate,
  locations, rooms, teachers,
  selectedRoomIds, onToggleLocation, onToggleRoom,
  teacherId, setTeacherId,
  teacherQuery, setTeacherQuery,
  hiddenLocationIds, setHiddenLocationIds,
  onReorderLocations,
  onlyFree, setOnlyFree,
  freeFrom, setFreeFrom,
  freeTo, setFreeTo,
}) {
  const [dragId, setDragId] = useState(null);
  const view = VIEW_TYPES.find((v) => v.id === viewType) || VIEW_TYPES[0];
  const visibleLocs = locations.filter((l) => !hiddenLocationIds.has(l.id));
  const roomsByLocation = visibleLocs.map((l) => ({
    location: l,
    rooms: rooms
      .filter((r) => r.location_id === l.id)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de')),
  })).filter((g) => g.rooms.length > 0);
  const filteredTeachers = teachers.filter((t) => matchesFreeText(t, teacherQuery));
  const selectedCount = (selectedRoomIds || []).length;

  return (
    <aside className="w-[250px] shrink-0 border-r border-slate-200 bg-slate-50 overflow-y-auto p-2 print:hidden" data-testid="calendar-sidebar">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Darstellung</div>
      <label className="text-[10px] text-slate-500">Typ</label>
      <select
        className="w-full h-8 text-xs border border-slate-200 rounded bg-white px-1 mb-1.5"
        value={viewType}
        onChange={(e) => setViewType(e.target.value)}
        data-testid="calendar-view-type"
      >
        {VIEW_TYPES.map((v) => (
          <option key={v.id} value={v.id}>{v.label}</option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-1.5 mb-2">
        <div>
          <label className="text-[10px] text-slate-500">Raster</label>
          <select className="w-full h-8 text-xs border border-slate-200 rounded bg-white px-1"
                  value={raster} onChange={(e) => setRaster(Number(e.target.value))} data-testid="calendar-raster">
            <option value={5}>5 Minuten</option>
            <option value={10}>10 Minuten</option>
            <option value={15}>15 Minuten</option>
            <option value={30}>30 Minuten</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Beginn</label>
          <select className="w-full h-8 text-xs border border-slate-200 rounded bg-white px-1"
                  value={beginHour} onChange={(e) => setBeginHour(Number(e.target.value))} data-testid="calendar-begin">
            {[7, 8, 9, 10, 11, 12, 13, 14].map((h) => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-1">
        <button type="button" className="flex-1 h-7 text-[10px] border border-slate-200 rounded bg-white hover:bg-slate-100"
                onClick={() => setCurrentDate(addDays(currentDate, view.span === 'month' ? -30 : view.span === 'week' ? -7 : -1))}
                data-testid="calendar-prev">‹</button>
        <button type="button" className="flex-1 h-7 text-[10px] border border-slate-200 rounded bg-white hover:bg-slate-100"
                onClick={() => setCurrentDate(new Date())} data-testid="calendar-today">heute</button>
        <button type="button" className="flex-1 h-7 text-[10px] border border-slate-200 rounded bg-white hover:bg-slate-100"
                onClick={() => {
                  const v = window.prompt('Datum (TT.MM.JJJJ)', toISO(currentDate).split('-').reverse().join('.'));
                  if (!v) return;
                  const m = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
                  if (m) setCurrentDate(new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])));
                }} data-testid="calendar-goto">Eingabe</button>
        <button type="button" className="flex-1 h-7 text-[10px] border border-slate-200 rounded bg-white hover:bg-slate-100"
                onClick={() => setCurrentDate(addDays(currentDate, 1))} data-testid="calendar-tomorrow">morgen</button>
        <button type="button" className="flex-1 h-7 text-[10px] border border-slate-200 rounded bg-white hover:bg-slate-100"
                onClick={() => setCurrentDate(addDays(currentDate, view.span === 'month' ? 30 : view.span === 'week' ? 7 : 1))}
                data-testid="calendar-next">›</button>
      </div>

      <MiniCal
        mode="single"
        selected={currentDate}
        onSelect={(d) => d && setCurrentDate(d)}
        weekStartsOn={1}
        className="p-0 scale-[0.92] origin-top"
      />

      {view.axis === 'rooms' && viewType === 'resources-vertical' && (
        <Section title="Nur freie">
          <div className="grid grid-cols-2 gap-1 mb-1">
            <input type="time" className="h-7 text-[11px] border rounded px-1" value={freeFrom}
                   data-testid="calendar-free-from"
                   onChange={(e) => {
                     const value = e.target.value;
                     setFreeFrom(value);
                     const hour = Number(String(value).slice(0, 2));
                     if (Number.isInteger(hour) && hour >= 0 && hour <= 23) setBeginHour(hour);
                   }} />
            <input type="time" className="h-7 text-[11px] border rounded px-1" value={freeTo}
                   data-testid="calendar-free-to"
                   onChange={(e) => setFreeTo(e.target.value)} />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <input type="checkbox" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)}
                   data-testid="calendar-only-free" />
            Nur freie Räume
          </label>
        </Section>
      )}

      {view.axis === 'teachers' ? (
        <Section title="Lehrkräfte | Personal">
          <div className="relative mb-1">
            <Search className="w-3 h-3 absolute left-2 top-2 text-slate-400" />
            <input className="w-full h-7 pl-6 text-xs border rounded bg-white"
                   placeholder="Suchen..." value={teacherQuery}
                   onChange={(e) => setTeacherQuery(e.target.value)}
                   data-testid="calendar-teacher-search" />
          </div>
          <div className="max-h-[42vh] overflow-y-auto">
            {filteredTeachers.map((t) => (
              <ListItem key={t.id} selected={teacherId === t.id}
                        onClick={() => setTeacherId(t.id)}
                        testId={`calendar-teacher-${t.id}`}>
                {t.last_name}, {t.first_name}
              </ListItem>
            ))}
          </div>
        </Section>
      ) : (
        <Section
          title="Veranstaltungsorte & Räume"
          action={(
            <>
              {selectedCount > 0 && (
                <span className="text-[9px] font-medium normal-case tracking-normal text-slate-400 mr-1">
                  {selectedCount} Räume
                </span>
              )}
              <Eye className="w-3 h-3 text-slate-400" />
            </>
          )}
        >
          <p className="px-1 mb-1 text-[10px] text-slate-400 leading-snug">
            {viewType === 'rooms-week'
              ? 'Einen Raum wählen. Oben stehen die Wochentage, darunter Unterricht und Reservierungen dieses Raums.'
              : 'Standort anklicken → alle Räume einblenden; erneut → ausblenden. Einzelne Räume darunter an-/abwählen.'}
          </p>
          <div className="max-h-[50vh] overflow-y-auto" data-testid="calendar-locations-list">
            {roomsByLocation.map(({ location: loc, rooms: locRoomList }) => {
              const state = locationSelectionState(loc.id, rooms, selectedRoomIds);
              const showRooms = state !== 'none';
              return (
                <div key={loc.id} className="mb-1"
                     onDragOver={(e) => {
                       if (!dragId || dragId === loc.id) return;
                       e.preventDefault();
                       e.dataTransfer.dropEffect = 'move';
                     }}
                     onDrop={(e) => {
                       e.preventDefault();
                       const fromId = e.dataTransfer.getData('text/plain') || dragId;
                       onReorderLocations?.(fromId, loc.id);
                       setDragId(null);
                     }}>
                  <SelectionRow
                    checked={state === 'all'}
                    indeterminate={state === 'some'}
                    onToggle={() => onToggleLocation?.(loc.id)}
                    testId={`calendar-location-${loc.id}`}
                    draggable={!!onReorderLocations}
                    dragging={dragId === loc.id}
                    onDragStart={(e) => {
                      setDragId(loc.id);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', loc.id);
                    }}
                    onDragEnd={() => setDragId(null)}
                    onRemove={() => setHiddenLocationIds((s) => new Set([...s, loc.id]))}
                  >
                    {loc.name}
                  </SelectionRow>
                  {showRooms && locRoomList.map((r) => (
                    <SelectionRow
                      key={r.id}
                      indent={1}
                      checked={(selectedRoomIds || []).includes(r.id)}
                      onToggle={() => onToggleRoom?.(r.id)}
                      testId={`calendar-room-${r.id}`}
                    >
                      {r.name}
                    </SelectionRow>
                  ))}
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </aside>
  );
}
